import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// --- CSS --- //
import "../styles/sign-up.css";

// --- Assets --- //
import logo from "../assets/vert-cert-logo.png";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import HomeIcon from '@mui/icons-material/Home';
import LoginIcon from '@mui/icons-material/Login';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

// --- FIREBASE --- //
import { handleEmailVerification, handleSignUp } from "../utilities/auth/authUtils";
import { showMessageWithDelay } from "../utilities/alertUtils";

const SignUp = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,
    includeUppercase: false,
    includeLowercase: false,
    includeNumbers: false,
    includeSymbols: false,
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validatePassword = (pwd) => {
    const criteria = {
      length: pwd.length >= 12,
      includeUppercase: /[A-Z]/.test(pwd),
      includeLowercase: /[a-z]/.test(pwd),
      includeNumbers: /[0-9]/.test(pwd),
      includeSymbols: /[!@#$%^&*()_\-+=\[\]{}|;:'",.<>?/`~]/.test(pwd),
    };
    setPasswordCriteria(criteria);
  };

  const autoGeneratePassword = () => {
    setShowPassword(true);

    const length = 12;
    let chars = '';
    chars += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'; // Uppercase
    chars += 'abcdefghijklmnopqrstuvwxyz'; // Lowercase
    chars += '0123456789'; // Numbers
    chars += '!@#$%^&*()_-+=[]{}|;:<>,.?/~'; // Symbols

    let newPassword = '';
    while (newPassword.length < length) {
      newPassword += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    // Shuffle password for randomness
    newPassword = newPassword.split('').sort(() => 0.5 - Math.random()).join('');
    setPassword(newPassword);
  };

  const onSignUpSubmit = async (e) => {
    e.preventDefault();
    try {
      await handleSignUp({ firstName, lastName, email, password, dateOfBirth });
      showMessageWithDelay("Account Created!", `${firstName}, one moment while we get things set up for you. Let's start climbing now...`, "success", 3000);
      setTimeout(() => handleEmailVerification(email, password, navigate, setError), 3000);
    } catch (err) {
      setError("An error occurred. Sign up unsuccessful.");
      console.log(err);
    }
  };

  useEffect(() => {
    validatePassword(password);
  }, [password]);

  return (
    <div className="login-page">
      <div className="login-window">
        <a href="/"><img src={logo} alt="Vert Cert Logo" className="logo" /></a>
        <a href="/login" className="login-link"><LoginIcon /></a>
        <a href="/" className="home-link"><HomeIcon /></a>
        <h2 className="login-title">Sign Up</h2>
        {error && <p className="error-message">{error}</p>}
        <form onSubmit={onSignUpSubmit}>
          <div className="input-container">
            <input
              style={{ marginRight: "5px" }}
              type="text"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
            <input
              style={{ marginLeft: "5px" }}
              type="text"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
          <label style={{ fontSize: "10px", textAlign: "left", display: "block" }}>Date of Birth</label>
          <input
            type="date"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <div className="password-container">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setIsPasswordFocused(true)}
              onBlur={() => setIsPasswordFocused(false)}
              required
            />
            <span className="auto-password" onClick={autoGeneratePassword}>
              Auto-Generate
            </span>
            <span className="toggle-password" onClick={togglePasswordVisibility}>
              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </span>
          </div>
          {isPasswordFocused && (
            <div className="password-checklist-container">
              <span style={{ color: passwordCriteria.includeUppercase ? "green" : "red" }}>
                {passwordCriteria.includeUppercase ? <CheckCircleIcon className="password-checklist-icon" /> : <CancelIcon className="password-checklist-icon" />} Contains a capital letter
              </span>
              <span style={{ color: passwordCriteria.includeLowercase ? "green" : "red" }}>
                {passwordCriteria.includeLowercase ? <CheckCircleIcon className="password-checklist-icon" /> : <CancelIcon className="password-checklist-icon" />} Contains a lowercase letter
              </span>
              <span style={{ color: passwordCriteria.includeNumbers ? "green" : "red" }}>
                {passwordCriteria.includeNumbers ? <CheckCircleIcon className="password-checklist-icon" /> : <CancelIcon className="password-checklist-icon" />} Contains a number
              </span>
              <span style={{ color: passwordCriteria.includeSymbols ? "green" : "red" }}>
                {passwordCriteria.includeSymbols ? <CheckCircleIcon className="password-checklist-icon" /> : <CancelIcon className="password-checklist-icon" />} Contains a special character
              </span>
              <span style={{ color: passwordCriteria.length ? "green" : "red" }}>
                {passwordCriteria.length ? <CheckCircleIcon className="password-checklist-icon" /> : <CancelIcon className="password-checklist-icon" />} Password is a minimum of 12 characters
              </span>
            </div>)}
          <button type="submit">Sign Up</button>
        </form>
      </div>
    </div>
  );
};

export default SignUp;